
import React from 'react';
import { ArrowLeft, BookOpen, ShieldCheck, Sparkles, Brain } from './Icons';
import { ViewState } from '../types';

export type BlogType = 'blog-how-it-works' | 'blog-best-apps' | 'blog-screen-free' | 'blog-safety-guide';

interface BlogPagesProps {
  type: BlogType;
  onBack: () => void;
  onNavigate: (view: ViewState) => void;
}

const blogContent: Record<BlogType, {
  title: string;
  metaDescription: string;
  keyword: string;
  publishDate: string;
  readingTime: string;
  icon: React.ReactNode;
  body: React.ReactNode;
}> = {
  'blog-how-it-works': {
    title: "How Personalized Bedtime Stories Work",
    metaDescription: "Learn how AI creates unique, personalized bedtime stories for your child — with their name, appearance, and favorite themes woven into every page.",
    keyword: "personalized bedtime stories",
    publishDate: "February 20, 2026",
    readingTime: "5 min read",
    icon: <Sparkles className="w-12 h-12 text-gold-400" />,
    body: (
      <div className="space-y-8">
        <p className="text-lg leading-relaxed">
          Every child deserves to be the hero of their own story. Personalized bedtime stories take your child's name, appearance, and interests and weave them into a unique narrative — making storytime feel truly magical.
        </p>

        <section className="space-y-3">
          <h2 className="text-2xl font-bold text-white">What Makes a Story "Personalized"?</h2>
          <p>Unlike generic storybooks, a personalized bedtime story features your child as the main character. Their name appears throughout the narrative, the illustrations reflect their appearance, and the themes match their interests — whether that's space adventures, magical forests, or animal friends.</p>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-bold text-white">How AI Brings Stories to Life</h2>
          <p>Modern AI story generators like StoryForge Kids use advanced language models to craft original narratives in seconds. Here's the process:</p>
          <ol className="list-decimal list-inside space-y-2 pl-4">
            <li><strong className="text-white">You create a child profile</strong> — name, age, appearance, and any favorite themes or values you want to reinforce (kindness, courage, curiosity).</li>
            <li><strong className="text-white">Choose your story settings</strong> — pick a genre (fairy tale, sci-fi, animal friends), tone (calm, funny, adventurous), and illustration style (watercolor, 3D Pixar, Studio Ghibli).</li>
            <li><strong className="text-white">AI writes and illustrates</strong> — the story engine generates an age-appropriate narrative with matching illustrations, all featuring your child as the protagonist.</li>
            <li><strong className="text-white">Read, listen, or print</strong> — enjoy the story as a digital picture book, listen with AI narration, or download a PDF to read offline.</li>
          </ol>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-bold text-white">Why Personalization Matters for Kids</h2>
          <p>Research in early childhood education shows that children engage more deeply with stories where they see themselves reflected. Personalized stories can:</p>
          <ul className="list-disc list-inside space-y-2 pl-4">
            <li>Boost reading motivation and attention span</li>
            <li>Strengthen emotional connection to the narrative</li>
            <li>Reinforce positive values in a relatable context</li>
            <li>Make bedtime something kids actually look forward to</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-bold text-white">Age-Appropriate Content at Every Level</h2>
          <p>The best personalized story platforms adjust vocabulary, sentence complexity, and page count based on your child's age. A story for a 3-year-old reads very differently from one designed for an 8-year-old — and that's exactly how it should be.</p>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-bold text-white">Getting Started Is Simple</h2>
          <p>With StoryForge Kids, you can create your first personalized bedtime story in under two minutes. Set up your child's profile once, and you'll have an endless library of unique stories tailored just for them.</p>
        </section>
      </div>
    )
  },

  'blog-best-apps': {
    title: "Best Bedtime Story Apps for Kids (2026)",
    metaDescription: "Compare the top bedtime story apps for kids in 2026. From AI-generated personalized tales to classic narrated stories — find the perfect fit for your family.",
    keyword: "best bedtime story apps",
    publishDate: "February 18, 2026",
    readingTime: "7 min read",
    icon: <BookOpen className="w-12 h-12 text-forge-400" />,
    body: (
      <div className="space-y-8">
        <p className="text-lg leading-relaxed">
          Finding the right bedtime story app can transform your nightly routine. With dozens of options available in 2026, we've compared the top contenders to help you choose the best fit for your family.
        </p>

        <section className="space-y-3">
          <h2 className="text-2xl font-bold text-white">What to Look for in a Bedtime Story App</h2>
          <p>Before diving into specific apps, here are the key features that separate great bedtime story apps from mediocre ones:</p>
          <ul className="list-disc list-inside space-y-2 pl-4">
            <li><strong className="text-white">Age-appropriate content</strong> — stories should match your child's reading level and developmental stage</li>
            <li><strong className="text-white">Audio narration</strong> — helpful for pre-readers and for creating a calming, hands-free bedtime routine</li>
            <li><strong className="text-white">Personalization</strong> — the ability to feature your child in the story increases engagement</li>
            <li><strong className="text-white">Safety and privacy</strong> — COPPA compliance and minimal data collection are essential</li>
            <li><strong className="text-white">Offline access</strong> — download or print options for screen-free reading</li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-bold text-white">Top Bedtime Story Apps in 2026</h2>

          <div className="bg-white/5 p-6 rounded-2xl border border-gold-400/30 space-y-2">
            <h3 className="text-xl font-bold text-gold-400">1. StoryForge Kids — Best for Personalized AI Stories</h3>
            <p>StoryForge Kids uses AI to generate completely original stories featuring your child as the main character. Each story comes with custom illustrations in your choice of art style (watercolor, 3D Pixar, Studio Ghibli, and more). Stories are age-calibrated and can be printed as PDFs or read with AI narration.</p>
            <p className="text-sm text-gray-400"><strong className="text-white">Best for:</strong> Parents who want unique, never-repeated stories personalized to their child.</p>
          </div>

          <div className="bg-white/5 p-6 rounded-2xl border border-white/10 space-y-2">
            <h3 className="text-xl font-bold text-white">2. Moonlit — Best for Classic Narrated Stories</h3>
            <p>Moonlit offers a curated library of classic bedtime tales with professional narration. It's a great option if you prefer traditional stories told by warm, engaging voices.</p>
            <p className="text-sm text-gray-400"><strong className="text-white">Best for:</strong> Families who love traditional fairy tales and classic stories.</p>
          </div>

          <div className="bg-white/5 p-6 rounded-2xl border border-white/10 space-y-2">
            <h3 className="text-xl font-bold text-white">3. Calm Kids — Best for Sleep-Focused Content</h3>
            <p>Part of the Calm meditation app family, Calm Kids focuses specifically on sleep stories designed to help children relax and drift off. The stories incorporate breathing exercises and calming narratives.</p>
            <p className="text-sm text-gray-400"><strong className="text-white">Best for:</strong> Children who have trouble falling asleep and need extra relaxation support.</p>
          </div>

          <div className="bg-white/5 p-6 rounded-2xl border border-white/10 space-y-2">
            <h3 className="text-xl font-bold text-white">4. Epic! — Best for a Large Library</h3>
            <p>Epic! offers thousands of books for kids across all age ranges. While not specifically a bedtime app, its massive library and read-to-me feature make it a versatile choice.</p>
            <p className="text-sm text-gray-400"><strong className="text-white">Best for:</strong> Voracious readers who want access to a huge variety of books.</p>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-bold text-white">The Verdict</h2>
          <p>If you want a fresh, unique story every night that features your child as the hero, StoryForge Kids is the clear winner. For families who prefer curated classics, Moonlit and Calm Kids are excellent alternatives. The best approach? Try a couple and see which one your child asks for at bedtime.</p>
        </section>
      </div>
    )
  },

  'blog-screen-free': {
    title: "Screen-Free Bedtime Routine in 10 Minutes",
    metaDescription: "Build a calming, screen-free bedtime routine for your child in just 10 minutes. Practical tips for parents who want to reduce screen time before sleep.",
    keyword: "screen-free bedtime routine",
    publishDate: "February 15, 2026",
    readingTime: "4 min read",
    icon: <Brain className="w-12 h-12 text-spark-400" />,
    body: (
      <div className="space-y-8">
        <p className="text-lg leading-relaxed">
          Screen time before bed is one of the biggest disruptors of children's sleep. The blue light from devices suppresses melatonin, and stimulating content keeps little brains wired. Here's how to build a calming, screen-free bedtime routine in just 10 minutes.
        </p>

        <section className="space-y-3">
          <h2 className="text-2xl font-bold text-white">Why Screen-Free Matters Before Bed</h2>
          <p>The American Academy of Pediatrics recommends stopping screen use at least 30–60 minutes before bedtime. Studies show that children who use screens before sleep take longer to fall asleep, get less total sleep, and experience lower sleep quality.</p>
          <p>But going screen-free doesn't mean giving up stories. In fact, physical or printed stories are one of the most effective bedtime wind-down activities.</p>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-bold text-white">The 10-Minute Screen-Free Bedtime Routine</h2>
          <p>Here's a simple framework that works for kids ages 2–10:</p>
          <div className="space-y-4 pl-4">
            <div>
              <h3 className="text-lg font-bold text-gold-400">Minutes 1–2: Transition Signal</h3>
              <p>Create a consistent cue that bedtime is starting. This could be dimming the lights, playing a specific soft song, or putting on pajamas together. Consistency is key — the same cue every night tells the brain it's time to wind down.</p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gold-400">Minutes 3–4: Connection Time</h3>
              <p>Spend two minutes on a brief, calming activity together. Talk about the best part of their day, share three things you're grateful for, or do some simple stretches. This emotional connection reduces bedtime resistance.</p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gold-400">Minutes 5–9: Story Time</h3>
              <p>Read a printed story together. This is where personalized stories shine — a child who sees themselves in the story is more engaged and more willing to settle into the routine. Print your StoryForge Kids stories as PDFs and keep a small stack by the bed.</p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gold-400">Minute 10: Goodnight Ritual</h3>
              <p>End with the same closing ritual every night: a hug, a kiss, a specific phrase ("Goodnight, sleep tight, dream of adventures tonight"). Predictability creates security.</p>
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-bold text-white">Tips for Making It Stick</h2>
          <ul className="list-disc list-inside space-y-2 pl-4">
            <li><strong className="text-white">Start the routine at the same time every night</strong> — even on weekends, try to stay within 30 minutes of your target time</li>
            <li><strong className="text-white">Prepare stories in advance</strong> — generate and print several stories during the weekend so you always have fresh ones ready</li>
            <li><strong className="text-white">Let your child choose</strong> — giving them a choice between 2–3 stories gives them agency while keeping you in control of the options</li>
            <li><strong className="text-white">Be patient during the transition</strong> — if your child is used to screens before bed, it may take 1–2 weeks to adjust to the new routine</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-bold text-white">The Printable Story Advantage</h2>
          <p>One of the best ways to maintain a screen-free bedtime is to have physical stories ready. StoryForge Kids lets you download any generated story as a PDF — print it out, and you have a personalized picture book with zero screens involved.</p>
        </section>
      </div>
    )
  },

  'blog-safety-guide': {
    title: "AI Stories for Kids: Safety Guide for Parents",
    metaDescription: "A parent's guide to AI-generated stories for kids. Learn how to evaluate safety, privacy, and content quality in AI story apps.",
    keyword: "AI stories for kids safety",
    publishDate: "February 12, 2026",
    readingTime: "6 min read",
    icon: <ShieldCheck className="w-12 h-12 text-green-400" />,
    body: (
      <div className="space-y-8">
        <p className="text-lg leading-relaxed">
          AI-generated stories for kids are a growing category, and parents have legitimate questions about safety, privacy, and content quality. This guide covers everything you need to know to make informed decisions about AI story apps for your family.
        </p>

        <section className="space-y-3">
          <h2 className="text-2xl font-bold text-white">Is AI-Generated Content Safe for Children?</h2>
          <p>The safety of AI-generated content depends entirely on the platform. Well-designed apps implement multiple safety layers:</p>
          <ul className="list-disc list-inside space-y-2 pl-4">
            <li><strong className="text-white">Content filtering</strong> — AI output is filtered to ensure age-appropriate language, themes, and imagery</li>
            <li><strong className="text-white">Structured prompts</strong> — the AI is given strict guidelines about what it can and cannot include</li>
            <li><strong className="text-white">Human-designed guardrails</strong> — story structure, emotional tone, and resolution are pre-defined by child development experts</li>
            <li><strong className="text-white">Quality scoring</strong> — some platforms (including StoryForge Kids) use an automated "critic" that scores each story on emotional authenticity, age-appropriateness, and safety before presenting it</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-bold text-white">Privacy: What Data Do AI Story Apps Collect?</h2>
          <p>This is one of the most important questions parents should ask. Here's what to look for:</p>
          <ul className="list-disc list-inside space-y-2 pl-4">
            <li><strong className="text-white">Minimal data collection</strong> — the app should only collect what's necessary to provide the service (child's first name and age, not full name, location, or school)</li>
            <li><strong className="text-white">COPPA compliance</strong> — any app used by children under 13 must comply with the Children's Online Privacy Protection Act</li>
            <li><strong className="text-white">Photo handling</strong> — if the app uses photos, understand where they're stored (local device vs. cloud), how they're processed, and whether they're used to train AI models</li>
            <li><strong className="text-white">Data deletion</strong> — you should be able to delete your child's data at any time</li>
          </ul>
          <p>At StoryForge Kids, photos are stored locally in your browser and are never used to train AI models. We collect only the minimum data needed to generate personalized stories.</p>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-bold text-white">How to Evaluate Story Quality</h2>
          <p>Not all AI stories are created equal. Here's how to judge the quality of AI-generated children's content:</p>
          <div className="space-y-4 pl-4">
            <div>
              <h3 className="text-lg font-bold text-gold-400">Narrative Coherence</h3>
              <p>Does the story have a clear beginning, middle, and end? Do events follow logically? Low-quality AI stories often meander or introduce random elements.</p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gold-400">Age Appropriateness</h3>
              <p>Are the vocabulary and sentence structure appropriate for your child's age? A story for a 3-year-old should be very different from one for an 8-year-old.</p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gold-400">Emotional Tone</h3>
              <p>Bedtime stories should end positively and calmly. Watch out for stories that introduce conflict without resolution or that might provoke anxiety at bedtime.</p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gold-400">Illustration Quality</h3>
              <p>AI illustrations should be consistent throughout the story. Characters should look the same on every page, and the art style should remain cohesive.</p>
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-bold text-white">Red Flags to Watch For</h2>
          <ul className="list-disc list-inside space-y-2 pl-4">
            <li>Apps that require children to create their own accounts (COPPA violation)</li>
            <li>No clear privacy policy or data handling explanation</li>
            <li>Stories with inconsistent or frightening AI-generated images</li>
            <li>Apps that upload children's photos to cloud servers without clear disclosure</li>
            <li>No ability to preview or review content before presenting it to your child</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-bold text-white">Our Safety Commitment</h2>
          <p>StoryForge Kids was built by parents, for parents. Every story goes through an automated quality and safety scoring system before it reaches your child. We're COPPA-compliant, we store photos locally on your device, and we never sell your data. Your child's safety is not just a feature — it's our foundation.</p>
        </section>
      </div>
    )
  }
};

const BlogPages: React.FC<BlogPagesProps> = ({ type, onBack, onNavigate }) => {
  const page = blogContent[type];

  return (
    <div className="min-h-screen bg-[#0f0e17] pt-10 px-4 pb-20">
      <div className="max-w-3xl mx-auto space-y-12 animate-slide-up">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-500 hover:text-white transition-colors group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="font-bold uppercase tracking-widest text-xs">Back to Home</span>
        </button>

        <div className="flex flex-col items-center text-center space-y-6">
          <div className="p-6 bg-white/5 rounded-full border border-white/10 shadow-xl">
            {page.icon}
          </div>
          <h1 className="text-4xl md:text-6xl font-serif font-bold text-white">{page.title}</h1>
          <div className="flex items-center gap-4 text-sm text-gray-400">
            <span>{page.publishDate}</span>
            <span className="w-1 h-1 bg-gray-600 rounded-full" />
            <span>{page.readingTime}</span>
            <span className="w-1 h-1 bg-gray-600 rounded-full" />
            <span>StoryForge Kids</span>
          </div>
        </div>

        <article className="glass-panel p-8 md:p-12 rounded-[3rem] border-white/10 text-gray-300 leading-relaxed font-light text-lg">
          {page.body}
        </article>

        <div className="text-center space-y-6 py-8">
          <p className="text-xl font-serif text-white">Ready to create a magical bedtime story?</p>
          <button
            onClick={() => onNavigate('landing')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-gold-400 to-gold-500 text-black font-bold rounded-2xl hover:scale-105 transition-transform shadow-xl"
          >
            <Sparkles className="w-5 h-5" />
            Try StoryForge Kids Free
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlogPages;
